// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AuthService } from 'src/services/auth-service';
import { UserService } from 'src/services/user-service';
import { NotificationService } from 'src/services/notification-service';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { AlertDialogComponent } from 'src/app/dialogs/alert/alert.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { LoginStatus} from '../../../types/user';
import { RestConfigService } from '../../../services/config-service';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { Theme, ThemeService } from '../../../services/theme.service';
import { ConfigurationService } from '../../../services/configuration-service';
import { CigentPoliciesURL, CigentTermsURL, LenovoPrivacyURL, Partners } from '../../../types/partners';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {loadUser} from '../../store/user/user.actions';
import {AppState} from '../../store/app.state';
import {Location} from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';

export const LOGIN_DEVICE_TOKEN = 'login-device-token';
export const STAY_LOGGED_IN = 'stay-logged-in';

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  @ViewChild('tooltip') tooltip: MatTooltip;
  currentYear = new Date().getFullYear();
  ready: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  outerBanner: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showStayLoggedIn = false;
  stayLoggedIn = true;
  showMSLogin = false;
  showOktaLogin = this.configuration.showOktaLogin;
  rememberMe = true;
  form: UntypedFormGroup;
  show2fa: boolean;
  route: ActivatedRoute;
  activationToken = '';
  usernameParam = '';
  loginError = false;
  timedOut = false;
  showLogin = true;
  showPasswordUpdateForm = false;
  private formSubmitAttempt: boolean;
  cigentLogin: boolean;
  deactivatedError = false;
  domainError = false;
  theme: Observable<Theme>;
  msButtonLightPath = '../../../assets/ms_signin_light.svg';
  msButtonDarkPath = '../../../assets/ms_signin_dark.svg';
  oktaButtonLightPath = '../../../assets/okta_signin_light.svg';
  oktaButtonDarkPath = '../../../assets/okta_signin_dark.svg';
  cigentPartner = Partners.Cigent;
  doqubizPartner = Partners.Doqubiz;
  digistorPartner = Partners.Digistor;
  kanguruPartner = Partners.Kanguru;
  lenovoPartner = Partners.Lenovo;
  vrsPartner = Partners.VRS;
  partnerId: Partners = Partners.Cigent;
  LenovoPrivacyURL = LenovoPrivacyURL;
  CigentPoliciesURL = CigentPoliciesURL;
  CigentTermsURL = CigentTermsURL;
  capsOn: boolean = false;
  passwordInputIsFocused = false;
  thirdPartyAuth = 'ms';

  fHeader: string;
  fInvalid: string;
  fEmailLabel: string;
  fEmail: string;
  fPassword: string;
  f2FAA: string;
  f2FAB: string;
  f2FAC: string;
  fRecovery: string;
  fSignIn: string;
  fChangePassword: string;
  fForgot: string;
  fCreateAccount: string;
  fDeactivated: string;

  constructor(
      private fb: UntypedFormBuilder,
      private el: ElementRef,
      private authService: AuthService,
      private userService: UserService,
      private notification: NotificationService,
      private dialog: MatDialog,
      private router: Router,
      private translate: TranslateService,
      private restConfig: RestConfigService,
      private themeService: ThemeService,
      private configuration: ConfigurationService,
      private readonly store: Store<AppState>,
      private location: Location,
      route: ActivatedRoute,
      ) {
    this.translate.get(['LI_HEADER', 'LI_INVALID', 'LI_EMAIL', 'LI_EMAIL_ADDRESS', 'LI_PASSWORD', 'LI_TWO_FACTOR_A', 'LI_DEACTIVATED',
                             'LI_TWO_FACTOR_B', 'LI_TWO_FACTOR_PHOLDER', 'LI_RECOVERY', 'LI_SIGNIN', 'LI_FORGOT', 'LI_CREATE',
                             'APP_CHANGE_PW']).subscribe(vals => {
      this.fHeader = vals['LI_HEADER'];
      this.fInvalid = vals['LI_INVALID'];
      this.fDeactivated = vals['LI_DEACTIVATED'];
      this.fEmailLabel = vals['LI_EMAIL'];
      this.fEmail = vals['LI_EMAIL_ADDRESS'];
      this.fPassword = vals['LI_PASSWORD'];
      this.f2FAA = vals['LI_TWO_FACTOR_A'];
      this.f2FAB = vals['LI_TWO_FACTOR_B'];
      this.f2FAC = vals['LI_TWO_FACTOR_PHOLDER'];
      this.fRecovery = vals['LI_RECOVERY'];
      this.fSignIn = vals['LI_SIGNIN'];
      this.fChangePassword = vals['APP_CHANGE_PW'];
      this.fForgot = vals['LI_FORGOT'];
      this.fCreateAccount = vals['LI_CREATE'];
    });
    this.route = route;
    this.cigentLogin = true;
    this.theme = this.themeService.getTheme();
    this.partnerId = this.configuration.partnerId ?? Partners.Cigent;
    this.restConfig.getRestConfig()
      .pipe(untilDestroyed(this))
      .subscribe(
        settings => {
          if (settings) {
            this.showMSLogin = settings.azure_active_directory_allowed && this.configuration.showAzureLogin;
          }
    });
    route.queryParamMap.subscribe((params: ParamMap) => {
          if (params.get('expiring') === 'true') {
            this.timedOut = true;
          }
          if (params.get('invalid_domain') === 'true') {
            this.domainError = true;
            // we need to remove the ?invalid_login=true or the url will not work with Active Directory
            this.location.replaceState('/login')
          }
        });
  }

  ngOnInit() {
    combineLatest([this.route.paramMap, this.route.queryParamMap])
        .subscribe(([params, queryParams]) => {

      this.activationToken = params.get('token');

      const deviceTokenParam = queryParams.get('device') || null;
      if (deviceTokenParam) {
        if (this.authService.isAuthenticated()) {
          this.authService.logout();
        }
        sessionStorage.setItem(LOGIN_DEVICE_TOKEN, deviceTokenParam);
        this.router.navigate(['/login']);
      }

      const destParam = queryParams.get('dest');
      if (destParam) {
        sessionStorage.setItem('login-dest', destParam);
        this.router.navigate(['/login']);
      }

      this.usernameParam = queryParams.get('u') || null;

      // indirect login--only show the page if not redirecting
      if (!deviceTokenParam) {
        this.ready.next(true);
      }

      const deviceToken = sessionStorage.getItem(LOGIN_DEVICE_TOKEN);
      if (deviceToken) {
        this.showStayLoggedIn = true;
      } else {
        this.outerBanner.next(true);
      }
    });

    this.form = this.fb.group({
      username: [this.usernameParam ? this.usernameParam : ''],
      password: [''],
      second_factor: null,
      stay_logged_in: [true]
    });

    if (this.route.snapshot && this.route.snapshot.fragment && this.route.snapshot.fragment.startsWith('id_token=')) {
      this.cigentLogin = !this.cigentLogin;
      this.clearForm(this.form);
    }
  }

  get deviceToken(): string {
    return sessionStorage.getItem(LOGIN_DEVICE_TOKEN);
  }

  toggleSigIn() {
    sessionStorage.setItem(STAY_LOGGED_IN, this.stayLoggedIn ? 'true' : 'false')
    this.cigentLogin = !this.cigentLogin;
    this.loginError = false;
    this.clearForm(this.form);
  }

  showMSLoginForm() {
    this.thirdPartyAuth = 'ms';
    this.toggleSigIn();
  }

  showOktaLoginForm(e) {
    e.preventDefault();
    this.thirdPartyAuth = 'okta';
    this.toggleSigIn();
  }

  onSubmit() {
    if (this.form.valid) {
      const deviceToken = sessionStorage.getItem(LOGIN_DEVICE_TOKEN);
      this.userService
          .userLogin(
            this.form.value.username,
            this.form.value.password,
            this.form.value.second_factor,
            this.activationToken,
            deviceToken,
            this.stayLoggedIn,
            this.configuration.partnerId
          )
          .subscribe(
            res => {
              if (res.status === LoginStatus.UNVERIFIED) {
                return this.unverifiedRedirect(res.email);
              } else if (res.status === LoginStatus.SUBSCRIBING) {
                return this.subscribingRedirect(res.email);
              } else if (res.status === LoginStatus.EXPIRED) {
                this.showPasswordUpdateForm = true;
              } else if (res.status === LoginStatus.PERMISSIVE_ENCRYPTION_DISABLED_FOR_LICENSE) {
                this.notification.showError(
                    this.translate.instant('ERROR_PERMISSIVE_ENCRYPTION_DISABLED_FOR_LICENSE'),
                    this.translate.instant('SIGN_IN_BLOCKED_TITLE'));
              } else if (res.status === LoginStatus.PERMISSIVE_ENCRYPTION_DISABLED_FOR_USER) {
                this.notification.showError(
                    this.translate.instant('ERROR_PERMISSIVE_ENCRYPTION_DISABLED_FOR_USER',
                    this.translate.instant('SIGN_IN_BLOCKED_TITLE')));
              } else if (res.status === LoginStatus.SUCCESS && deviceToken) {
                sessionStorage.removeItem(LOGIN_DEVICE_TOKEN);
                this.router.navigate(['/login-success']);
              } else if ( res.status === LoginStatus.INVALID_DOMAIN) {
                 if ( res?.login_url ) {
                    window.location.href = res.login_url + '?invalid_domain=true';
                 }
              } else if (res.status === LoginStatus.TWO_FACTOR_ENABLED) {
                this.loginError = false;
                this.showLogin = false;
                this.show2fa = true;
                this.deactivatedError = false;
              } else if (res.status === LoginStatus.DEACTIVATED) {
                this.loginError = false;
                this.showLogin = true;
                this.show2fa = false;
                this.deactivatedError = true;
              } else {
                if (res.status === LoginStatus.NEEDS_POLICY_ACCEPTANCE) {
                  const translatePostfix = this.configuration.translatePostfix;
                  const firstLine = this.translate.instant('accept-policies.first-line' + translatePostfix);
                  const secondLine = this.translate.instant('accept-policies.second-line' + translatePostfix);
                  const thirdLine = `${this.translate.instant('accept-policies.third-line' + translatePostfix)}`;

                  const dialogRef = this.dialog.open(AlertDialogComponent, {
                      width: '515px',
                      data: {
                        title: this.translate.instant('accept-policies.title'),
                        unsafeHTML: `<div>${firstLine}</div>` +
                            `<div>${secondLine}</div></br>` +
                            `<div>${thirdLine}</div>`,
                        acceptBtnName: this.translate.instant('CONTINUE'),
                        rejectBtnName: this.translate.instant('CANCEL'),
                        showRejectBtn: true,
                      },
                    });

                  dialogRef.afterClosed().subscribe(dialogResult => {
                    if (dialogResult) {
                      this.userService.acceptCigentPolicies(res.token)
                          .subscribe(() => {
                            this.onSubmit();
                          });
                    } else {
                      this.form.get('username').setValue('');
                      this.form.get('password').setValue('');
                      this.authService.logout();
                    }
                  });
                } else {
                  const destParam = sessionStorage.getItem(('login-dest'))
                  sessionStorage.removeItem('login-dest')
                  this.authService.login(res.token, res.mapToken, this.rememberMe, this.activationToken, destParam);
                  // We do NOT want to destroy early, let this finish.
                  // Do NOT use .pipe(untilDestroyed(this))
                  this.store.dispatch(loadUser());
                }
              }
            },
            error => {
                if (error.status === LoginStatus.FAILED) {
                    this.loginError = true;
                    this.deactivatedError = false;
                    if (this.show2fa) {
                        this.form.get('second_factor').setValue(null);
                        this.form.get('second_factor').setErrors(null);
                    }
                    this.showLogin = true;
                    this.show2fa = false;
                    setTimeout(this.clearForm, 0, this.form);
                } else if (error.status === LoginStatus.ACCOUNT_LOCKED) {
                    this.loginError = true;
                    this.showLogin = true;
                    this.show2fa = false;
                    this.deactivatedError = false;
                    this.accountLocked();
                } else if (error.status === LoginStatus.PERMISSIVE_ENCRYPTION_DISABLED_FOR_LICENSE ||
                  error.status === LoginStatus.PERMISSIVE_ENCRYPTION_DISABLED_FOR_USER) {
                    this.loginError = false;
                    this.showLogin = true;
                    this.show2fa = false;
                    this.deactivatedError = true;
                }
           }
        );
    }
    this.formSubmitAttempt = true;
  }

  clearForm(form) {
    form.get('username').setValue('');
    form.get('password').setValue('');
  }

  accountLocked() {
    const vals = this.translate.instant(['ACCOUNT_LOCKED_TITLE', 'ACCOUNT_LOCKED_CONTENT', 'ALERT_OK']);
    this.dialog.open(AlertDialogComponent, {
      width: '450px',
      data: {
        title: vals['ACCOUNT_LOCKED_TITLE'],
        content: vals['ACCOUNT_LOCKED_CONTENT'],
        acceptBtnName: vals['ALERT_OK'],
        showRejectBtn: false,
      },
    });
  }

  onCapsLockChanged(event: boolean) {
    this.capsOn = event && this.passwordInputIsFocused;
  }

  passwordFocus() {
    this.passwordInputIsFocused = true;
  }

  passwordBlur() {
    this.passwordInputIsFocused = false;
  }

  private unverifiedRedirect(email: string) {
    const emailParam = encodeURIComponent(email);
    return this.router.navigate(['subscriptions', 'plustrial'], {
      queryParams: {
        verify: emailParam
      }
    });
  }

  private subscribingRedirect(email: string) {
    const emailParam = encodeURIComponent(email);
    return this.router.navigate(['subscriptions', 'd3e'], {
      queryParams: {
        address: emailParam
      }
    });
  }
}
